'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';

import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';

import { A11y, Navigation } from 'swiper/modules';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';

export const ProductGallerySwiper = ({ product }: { product: ProductDetailData }) => {
  const { t } = useTranslations();
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const fallbackImages = product.images || [];
  const images = selectedVariant?.images?.length ? selectedVariant.images : fallbackImages;
  const galleryImages = images.slice(1);
  const hasPossibleGalleryImages =
    fallbackImages.length > 1 || product.variants?.some(variant => (variant.images?.length ?? 0) > 1);

  if (!hasPossibleGalleryImages) {
    return null;
  }

  return (
    <div
      className={cn(
        'relative h-[50vh] w-full @[1920px]:h-[82vh] md:h-[72vh] lg:h-[70vh] xl:h-[71vh] 2xl:h-[75vh]',
        'ea-product-gallery__reserved-space',
      )}
    >
      {!!galleryImages.length && (
        <Swiper
          navigation={true}
          modules={[Navigation, A11y]}
          a11y={{ prevSlideMessage: t('text3525'), nextSlideMessage: t('text3526') }}
          className={cn('mySwiper', 'h-full w-full', 'ea-product-gallery__swiper')}
          slidesPerView={1}
          spaceBetween={0}
        >
          {galleryImages.map((image, index) => (
            <SwiperSlide key={`${image.url}-${index}`} className="relative !h-full">
              <Image
                src={image.url}
                alt={resolveImageAlt(image, product?.name)}
                fill
                style={{ objectFit: 'cover', objectPosition: '50% 20%' }}
                sizes="100vw"
                loading={index === 0 ? 'eager' : 'lazy'}
                fetchPriority={index === 0 ? 'high' : 'auto'}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  );
};
